import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DayPlanVisitDto, PatientVisitDto, TrialVisitType } from '@ctms/contracts';
import { copyScheduleToPerformed, nowAsRegistration, type CalendarPoint } from '@ctms/shared';
import { z } from 'zod';
import { AlertCircle } from 'lucide-react';
import { isHTTPError } from 'ky';

import { getApiErrorMessage } from '../../../api/client.js';
import { visitsApi } from '../visits.api.js';
import { visitQueryKeys } from '../visits.query-keys.js';
import { VisitPersonnelSection } from './visit-personnel-section.js';

const VISIT_TYPE_LABELS: Record<TrialVisitType, string> = {
  S: 'Ośrodek',
  P: 'Telefon',
  H: 'Dom',
  I: 'Ośrodek + lek badany',
};

const RegisterVisitFormSchema = z.object({
  date: z.string().trim().min(1, 'Data przeprowadzenia jest wymagana.'),
  time: z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Godzina musi być w formacie GG:MM (HH:MM)')
    .or(z.literal(''))
    .optional(),
  type: z.enum(['S', 'P', 'H', 'I']),
  mainComment: z.string().max(2000).optional(),
});

type RegisterVisitFormInput = z.infer<typeof RegisterVisitFormSchema>;

/**
 * Everything the cockpit needs to open the registration window, independent of
 * the entry point (REQ-VIS-018/021). The day plan opens it for a booked visit
 * (`dayPlanRow` set - the procedures entry stays available); the patient chart
 * opens it for ANY visit of the plan, including one that was never booked
 * (`scheduledDate: null`, `dayPlanRow: null`). `DayPlanVisitDto` cannot serve
 * as this state: its `date` is non-nullable, and an unbooked visit has none.
 */
export interface VisitRegistrationTarget {
  visitId: number;
  visitLabel: string;
  patientName: string;
  scheduledDate: string | null;
  scheduledTime: string | null;
  performedDate: string | null;
  performedTime: string | null;
  currentType: TrialVisitType;
  currentComment: string | null;
  /** The day-plan row when opened from the day plan; null from the chart. */
  dayPlanRow: DayPlanVisitDto | null;
}

export interface VisitRegistrationProps {
  visitId: number;
  /** The trial of this visit — the R8 personnel picker reads its site roles and team (#32). */
  trialId: number;
  visitLabel: string;
  patientName: string;
  scheduledDate: string | null;
  scheduledTime: string | null;
  /** Performed date already stored; empty when the visit is still planned. */
  performedDate: string | null;
  performedTime: string | null;
  currentType: TrialVisitType;
  currentComment: string | null;
  canWrite: boolean;
  /** Injected clock for REQ-VIS-022; tests pass a fixed point. */
  now?: CalendarPoint;
  onSaved?: (visit: PatientVisitDto) => void;
  /** Leaves the window without saving (R `#close-registration`); hidden when absent. */
  onClose?: () => void;
  /**
   * Opens the procedure registration of this visit (REQ-VIS-030, makieta card 2).
   * The entry is part of the registration window itself — out of the visit-data
   * form — so the panel only renders it when the caller wires the navigation.
   */
  onOpenProcedures?: () => void;
}

const fieldClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100';
const labelClass = 'text-xs text-slate-400';

function localNow(): CalendarPoint {
  const n = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return {
    date: `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`,
    time: `${pad(n.getHours())}:${pad(n.getMinutes())}`,
  };
}

/**
 * REQ-VIS-020…026/028 + REQ-VIS-027: the visit-data form of the registration
 * window (makieta `visit-registration.ui.md`, R1–R8 + R9). Personnel (R8) is
 * the separate "who performed it" register — saved with a set-replace PUT
 * right after the registration PATCH (#32). The out-of-window reason (R5 /
 * REQ-VIS-024) stays out of this atom.
 */
export function VisitRegistration({
  visitId,
  trialId,
  visitLabel,
  patientName,
  scheduledDate,
  scheduledTime,
  performedDate,
  performedTime,
  currentType,
  currentComment,
  canWrite,
  now,
  onSaved,
  onClose,
  onOpenProcedures,
}: VisitRegistrationProps): JSX.Element {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRevokeOpen, setIsRevokeOpen] = useState(false);
  const isRegistered = performedDate !== null && performedDate !== '';

  // R8 (#32): the personnel register of this visit. `null` = the user has not
  // touched the section yet — the SAVED register (server) is the selection.
  const [selectedPersonnel, setSelectedPersonnel] = useState<ReadonlySet<number> | null>(null);
  const personnelQuery = useQuery({
    queryKey: visitQueryKeys.visitPersonnel(visitId),
    queryFn: () => visitsApi.getVisitPersonnel(visitId),
    retry: (count, error) => (isHTTPError(error) ? false : count < 3),
  });
  const selectedIds =
    selectedPersonnel ??
    new Set((personnelQuery.data?.items ?? []).map((item) => item.trialPersonnelId));

  const onTogglePersonnel = (assignmentId: number): void => {
    const next = new Set(selectedIds);
    if (next.has(assignmentId)) {
      next.delete(assignmentId);
    } else {
      next.add(assignmentId);
    }
    setSelectedPersonnel(next);
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterVisitFormInput>({
    resolver: zodResolver(RegisterVisitFormSchema),
    defaultValues: {
      date: performedDate ?? '',
      time: performedTime ?? '',
      type: currentType,
      mainComment: currentComment ?? '',
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (input: RegisterVisitFormInput) => {
      const visit = await visitsApi.registerVisit(visitId, {
        date: input.date,
        time: input.time && input.time.length > 0 ? input.time : null,
        type: input.type,
        mainComment: input.mainComment && input.mainComment.length > 0 ? input.mainComment : null,
      });
      // R8 (#32): the personnel register travels with the registration — the
      // PUT lands only after the visit IS registered (the server refuses
      // personnel on an unregistered visit).
      await visitsApi.saveVisitPersonnel(visitId, {
        items: [...selectedIds].map((trialPersonnelId) => ({ trialPersonnelId })),
      });
      return visit;
    },
    onSuccess: async (visit) => {
      setSelectedPersonnel(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: visitQueryKeys.dayPlans() }),
        queryClient.invalidateQueries({ queryKey: visitQueryKeys.visitPersonnel(visitId) }),
      ]);
      onSaved?.(visit);
    },
    onError: async (err: unknown) => {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać rejestracji.'));
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => visitsApi.registerVisit(visitId, { date: null }),
    onSuccess: async (visit) => {
      setIsRevokeOpen(false);
      // The server DELETED the personnel register together with the
      // registration (revoke-clears-personnel, #32) — drop the local
      // selection and refetch the (now empty) register.
      setSelectedPersonnel(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: visitQueryKeys.dayPlans() }),
        queryClient.invalidateQueries({ queryKey: visitQueryKeys.visitPersonnel(visitId) }),
      ]);
      onSaved?.(visit);
    },
    onError: async (err: unknown) => {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się cofnąć rejestracji.'));
    },
  });

  const busy = saveMutation.isPending || revokeMutation.isPending;

  const onSetNow = (): void => {
    const filled = nowAsRegistration(now ?? localNow());
    setValue('date', filled.performedDate ?? '', { shouldValidate: true });
    setValue('time', filled.performedTime ?? '', { shouldValidate: true });
  };

  const onCopySchedule = (): void => {
    const copied = copyScheduleToPerformed({ scheduledDate, scheduledTime });
    if (copied.performedDate === null) {
      return;
    }
    setValue('date', copied.performedDate, { shouldValidate: true });
    setValue('time', copied.performedTime ?? '', { shouldValidate: true });
  };

  const onSubmit = (input: RegisterVisitFormInput): void => {
    setErrorMessage(null);
    saveMutation.mutate(input);
  };

  if (!canWrite) {
    return (
      <section className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100">
        <h2 className="text-sm font-semibold">Brak dostępu do rejestracji wizyt</h2>
        <p className="mt-2 text-xs text-slate-400">
          Nie możesz rejestrować ani cofać wizyt w tym ośrodku.
        </p>
      </section>
    );
  }

  const plannedLabel =
    scheduledDate === null
      ? 'brak — rejestracja bez wcześniejszego umówienia jest dozwolona'
      : `${scheduledDate}${scheduledTime ? ` · ${scheduledTime}` : ''}`;

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100">
      <header className="border-b border-slate-800 pb-4">
        <h2 className="text-sm font-semibold">
          {isRegistered ? `Przegląd rejestracji — ${visitLabel}` : `Rejestracja wizyty — ${visitLabel}`}
        </h2>
        <p className="mt-1 text-xs text-slate-400">Pacjent: {patientName}</p>
      </header>

      <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <legend className={labelClass}>Data i godzina przeprowadzenia</legend>
          <div className="mt-1 flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="sr-only">Data przeprowadzenia</span>
              <input type="date" className={fieldClass} {...register('date')} />
            </label>
            <label className="block">
              <span className="sr-only">Godzina przeprowadzenia</span>
              <input type="time" className={fieldClass} {...register('time')} />
            </label>
            <button
              type="button"
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800"
              onClick={onSetNow}
            >
              TERAZ
            </button>
            <button
              type="button"
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800 disabled:opacity-40"
              onClick={onCopySchedule}
              disabled={scheduledDate === null}
            >
              {'<< z planu'}
            </button>
          </div>
          {errors.date ? <p className="mt-1 text-xs text-rose-400">{errors.date.message}</p> : null}
          {errors.time ? <p className="mt-1 text-xs text-rose-400">{errors.time.message}</p> : null}
        </fieldset>

        <p className="text-xs text-slate-400">
          Termin zaplanowany: <strong className="text-slate-200">{plannedLabel}</strong>
        </p>

        <label className="block">
          <span className={labelClass}>Typ wizyty</span>
          <select className={fieldClass} {...register('type')}>
            {(Object.keys(VISIT_TYPE_LABELS) as TrialVisitType[]).map((code) => (
              <option key={code} value={code}>
                {VISIT_TYPE_LABELS[code]}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className={labelClass}>Komentarz główny</span>
          <textarea className={`${fieldClass} min-h-16`} {...register('mainComment')} />
        </label>

        <VisitPersonnelSection
          trialId={trialId}
          selected={selectedIds}
          onToggle={onTogglePersonnel}
          disabled={busy}
        />

        {errorMessage ? (
          <p className="flex items-start gap-2 text-xs text-rose-400" role="alert">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {errorMessage}
          </p>
        ) : null}

        <footer className="flex items-center justify-between gap-2 border-t border-slate-800 pt-4">
          {isRegistered ? (
            <button
              type="button"
              className="rounded-md border border-rose-800 px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-950"
              onClick={() => setIsRevokeOpen(true)}
              disabled={busy}
            >
              Cofnij rejestrację
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            {onClose ? (
              <button
                type="button"
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800"
                onClick={onClose}
                disabled={busy}
              >
                Anuluj
              </button>
            ) : null}
            <button
              type="submit"
              className="rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              disabled={busy}
            >
              Zapisz
            </button>
          </div>
        </footer>
      </form>

      {onOpenProcedures ? (
        <section className="mt-4 rounded-lg border border-slate-800 bg-slate-950/40 p-4">
          <h3 className="text-xs font-semibold text-slate-200">Procedury dodatkowe</h3>
          <p className="mt-1 text-xs text-slate-400">
            Rejestracja procedur to osobny krok wizyty (REQ-VIS-030).
          </p>
          <button
            type="button"
            className="mt-2 rounded-md border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800 disabled:opacity-50"
            onClick={onOpenProcedures}
            disabled={busy}
          >
            Otwórz rejestrację procedur
          </button>
        </section>
      ) : null}

      {isRevokeOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="revoke-registration-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
        >
          <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6">
            <h3 id="revoke-registration-title" className="text-sm font-semibold">
              Czy na pewno cofnąć rejestrację wizyty?
            </h3>
            <p className="mt-2 text-xs text-slate-400">
              Wizyta wróci do stanu sprzed rejestracji. Data umówienia pozostaje bez zmian.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800"
                onClick={() => setIsRevokeOpen(false)}
                disabled={busy}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="rounded-md bg-rose-800 px-3 py-1.5 text-xs text-white hover:bg-rose-700"
                onClick={() => {
                  setErrorMessage(null);
                  revokeMutation.mutate();
                }}
                disabled={busy}
              >
                Cofnij rejestrację
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
